import { pinia } from '@/store'
import { useSessionStore } from '@/store/session'

const AUTH_REDIRECT_KEY = 'adapter_auth_redirect_at'
const AUTH_REDIRECT_TARGET_KEY = 'adapter_auth_redirect_target'
const AUTH_REDIRECT_TTL = 5 * 60 * 1000
let refreshPromise = null

export async function fetchGatewaySession() {
  try {
    const sessionToken = useSessionStore(pinia).accessToken
    const response = await fetch('/bff/me', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
    })
    const payload = await response.json().catch(() => ({}))
    const result = payload?.result || payload
    const accessToken = extractAccessToken(response, result)
    return {
      status: response.status,
      ok: response.ok,
      data: accessToken ? { ...(result || {}), accessToken } : result,
      message: payload?.message || payload?.error || '',
      raw: payload,
    }
  } catch (error) {
    return {
      status: 0,
      ok: false,
      data: null,
      message: error?.message || 'gateway session request failed',
      raw: null,
    }
  }
}

export async function refreshGatewayAccessToken() {
  if (!refreshPromise) {
    refreshPromise = requestGatewayRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

async function requestGatewayRefresh() {
  try {
    const response = await fetch('/bff/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
    const payload = await response.json().catch(() => ({}))
    const result = payload?.result || payload
    const accessToken = extractAccessToken(response, result)
    return {
      status: response.status,
      ok: response.ok,
      data: accessToken ? { ...(result || {}), accessToken } : result,
      message: payload?.message || payload?.error || '',
      raw: payload,
    }
  } catch (error) {
    return {
      status: 0,
      ok: false,
      data: null,
      message: error?.message || 'gateway refresh request failed',
      raw: null,
    }
  }
}

function extractAccessToken(response, payload) {
  const candidates = [
    payload?.accessToken,
    payload?.access_token,
    payload?.token,
    response?.headers?.get('X-Access-Token'),
    response?.headers?.get('Authorization'),
  ]
  for (const candidate of candidates) {
    const value = normalizeBearerToken(candidate)
    if (value) return value
  }
  return ''
}

function normalizeBearerToken(token) {
  const value = String(token || '').trim()
  if (!value) return ''
  if (value.toLowerCase().startsWith('bearer ')) {
    return value.slice(7).trim()
  }
  return value
}

export function redirectToGatewayLogin(targetPath = window.location.pathname + window.location.search) {
  const normalizedTargetPath = normalizeTargetPath(targetPath)
  try {
    sessionStorage.setItem(AUTH_REDIRECT_KEY, String(Date.now()))
    sessionStorage.setItem(AUTH_REDIRECT_TARGET_KEY, normalizedTargetPath)
  } catch (e) {
    // ignore storage failures
  }
  const returnTo = new URL(normalizedTargetPath, window.location.origin)
  window.location.assign(`/bff/login/start?return_to=${encodeURIComponent(returnTo.toString())}`)
}

export function hasRecentGatewayRedirect() {
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_KEY)
    if (!raw) return false
    const ts = Number(raw)
    return Number.isFinite(ts) && Date.now()-ts <= AUTH_REDIRECT_TTL
  } catch (e) {
    return false
  }
}

export function clearGatewayRedirectFlag() {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_KEY)
    sessionStorage.removeItem(AUTH_REDIRECT_TARGET_KEY)
  } catch (e) {
    // ignore storage failures
  }
}

export function consumeGatewayRedirectTarget() {
  try {
    const value = sessionStorage.getItem(AUTH_REDIRECT_TARGET_KEY) || '/'
    sessionStorage.removeItem(AUTH_REDIRECT_TARGET_KEY)
    return value
  } catch (e) {
    return '/'
  }
}

function normalizeTargetPath(targetPath) {
  const value = String(targetPath || '').trim()
  if (!value) return '/'
  try {
    const resolved = new URL(value, window.location.origin)
    if (resolved.origin !== window.location.origin) return '/'
    return `${resolved.pathname}${resolved.search}` || '/'
  } catch (e) {
    return value.startsWith('/') ? value : '/'
  }
}

export function extractAuthFailure(search = window.location.search) {
  const params = new URLSearchParams(search || '')
  const reason = params.get('reason') || params.get('error') || ''
  const message = params.get('message') || params.get('error_description') || ''
  if (!reason && !message) return null
  return { reason, message }
}
